/**
 * Parses various date input formats into a JavaScript Date object.
 * Handles Unix timestamps (seconds/milliseconds), ISO strings, date arrays, and YYYY-MM-DD format.
 * 
 * @param dateInput - Date in various formats: string, number (timestamp), array [year, month, day], or null/undefined
 * @returns Parsed Date object or null if parsing fails
 */
export const parseDate = (dateInput: string | number | number[] | null | undefined): Date | null => {
    if (dateInput === null || dateInput === undefined) {
        return null;
    }

    try {
        if (Array.isArray(dateInput)) {
            if (dateInput.length === 3 && dateInput.every(n => typeof n === 'number')) {
                const [year, month, day] = dateInput;
                return new Date(year, month - 1, day, 12, 0, 0);
            }
        } else if (typeof dateInput === 'number') {
            const timestamp = dateInput < 946684800000 ? dateInput * 1000 : dateInput;
            return new Date(timestamp);
        } else {
            if (dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [year, month, day] = dateInput.split('-').map(Number);
                return new Date(year, month - 1, day, 12, 0, 0);
            } else {
                return new Date(dateInput);
            }
        }
    } catch (error) {
        console.error('Error parsing date:', dateInput, error);
    }
    return null;
};

/**
 * Formats a date with relative context (Today/Yesterday) or absolute format.
 * Shows "Today" for today's date, "Yesterday" for yesterday, otherwise formats as "day month year".
 * 
 * @param date - Date object
 * @returns Formatted date string (e.g., "Today", "Yesterday", "10 Aug 2025") or "Invalid Date"
 */

const formatDateRelative = (date: Date): string => {
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';
    
    return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
};

/**
 * Formats a date in standard "day month year" format.
 * Uses British locale format for consistent display across the application.
 * 
 * @param dateInput - Date in various formats: string, number (timestamp), array [year, month, day], or null/undefined
 * @returns Formatted date string (e.g., "10 Aug 2025") or "Invalid Date"
 */
export const formatDate = (dateInput: string | number | number[] | null | undefined) => {
    const date = parseDate(dateInput);
    if (!date || isNaN(date.getTime())) {
        return 'Invalid Date';
    }

    return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
};

/**
 * Extracts and formats the time portion from a date.
 * Uses 12-hour format with AM/PM notation.
 * 
 * @param dateInput - Date in various formats: string, number (timestamp), array [year, month, day], or null/undefined
 * @returns Formatted time string (e.g., "2:30 PM") or empty string if invalid
 */
const formatTime = (dateInput: string | number | number[] | null | undefined) => {
    const date = parseDate(dateInput);
    if (!date || isNaN(date.getTime())) {
        return '';
    }

    return date.toLocaleTimeString('en-GB', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

const toDateTime = (dateInput: string | null | undefined) => {
    const date = parseDate(dateInput);
    if (!date || isNaN(date.getTime())) {
        return '';
    }

    return new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    }).format(date);
}

const formatTimelineDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    const day = date.getDate().toString();
    return { month, day };
};

/**
 * Formats a date as relative time showing how long ago it was updated.
 * Provides human-readable time differences like "just now", "5 minutes ago", "2 hours ago", etc.
 * 
 * @param dateInput - Date in various formats: string, number (timestamp), array [year, month, day], or null/undefined
 * @returns Human-readable time ago string (e.g., "Updated 5 minutes ago", "Updated yesterday") or fallback message
 */
const formatTimeAgo = (dateInput: string | number | number[] | null | undefined) => {
    const date = parseDate(dateInput);
    if (!date || isNaN(date.getTime())) {
        return 'Updated some time ago';
    }

    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) {
        return 'Updated just now';
    } else if (diffInMinutes < 60) {
        return `Updated ${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    } else if (diffInHours < 24) {
        return `Updated ${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    } else if (diffInDays === 1) {
        return 'Updated yesterday';
    } else {
        return `Updated ${diffInDays} days ago`;
    }
};

/**
 * Formats a date with varied "Updated" text formats for variety in UI display.
 * Uses different phrasings to avoid repetitive "Updated X ago" patterns.
 * 
 * @param dateInput - Date in various formats: string, number (timestamp), array [year, month, day], or null/undefined
 * @returns Varied human-readable time strings (e.g., "Updated recently", "Updated 20 minutes ago", "Updated 7 days ago") or fallback message
 */
const formatTimeAgoVaried = (dateInput: string | number | number[] | null | undefined) => {
    const date = parseDate(dateInput);
    if (!date || isNaN(date.getTime())) {
        return 'Updated some time ago';
    }

    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 5) {
        return 'Updated recently';
    } else if (diffInMinutes < 60) {
        return `Updated ${diffInMinutes} minutes ago`;
    } else if (diffInHours === 1) {
        return 'Updated one hour ago';
    } else if (diffInHours < 24) {
        return `Updated ${diffInHours} hours ago`;
    } else if (diffInDays === 7) {
        return 'Updated 7 days ago';
    } else if (diffInDays <= 30) {
        return `Updated ${diffInDays} days ago`;
    } else {
        const weeks = Math.floor(diffInDays / 7);
        return `Updated ${weeks} week${weeks === 1 ? '' : 's'} ago`;
    }
};

/**
 * Formats a timestamp in the "Today, X minutes ago" format for story timestamps.
 * Matches Figma design specification for story display.
 * 
 * @param dateInput - Date in various formats: string, number (timestamp), array [year, month, day], or null/undefined
 * @returns Formatted timestamp string (e.g., "Today, 5 minutes ago", "Yesterday, 5:39 pm") or fallback
 */
export const formatStoryTimestamp = (dateInput: string | number | number[] | null | undefined) => {
    const date = parseDate(dateInput);
    if (!date || isNaN(date.getTime())) {
        return 'Today, just now';
    }

    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();
    
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);

    if (isToday) {
        if (diffInMinutes < 1) {
            return 'Today, just now';
        } else if (diffInMinutes < 60) {
            return `Today, ${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
        } else {
            return `Today, ${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
        }
    } else if (isYesterday) {
        const formattedTime = date.toLocaleTimeString('en-GB', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        return `Yesterday, ${formattedTime}`;
    } else {
        // For older dates, use the relative format
        return formatDateRelative(date);
    }
};

/**
 * Formats a timestamp for generated story cards.
 * For today's stories: shows "20 minutes ago" (without "Today" prefix)
 * For yesterday or older: shows "Yesterday, 2:03 pm" format
 * 
 * @param dateInput - Date in various formats: string, number (timestamp), array [year, month, day], or null/undefined
 * @returns Formatted timestamp string (e.g., "20 minutes ago", "Yesterday, 2:03 pm") or fallback
 */
export const formatGeneratedStoryTimestamp = (dateInput: string | number | number[] | null | undefined) => {
    const date = parseDate(dateInput);
    if (!date || isNaN(date.getTime())) {
        return 'just now';
    }

    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();
    
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);

    if (isToday) {
        if (diffInMinutes < 1) {
            return 'just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
        } else {
            return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
        }
    } else if (isYesterday) {
        const formattedTime = date.toLocaleTimeString('en-GB', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        return `Yesterday, ${formattedTime}`;
    } else {
        // For older dates, use the relative format
        return formatDateRelative(date);
    }
};

export const formatChatTimestamp = (date: Date): string => {
    if (isNaN(date.getTime())) {
        return '';
    }

    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, '0');
        return `${displayHours}:${displayMinutes} ${ampm}`;
    }

    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
    });
};

/**
 * Formats a timestamp in compact relative format for artifact versioning.
 * Follows Claude Artifacts pattern: "just now", "3m ago", "2h ago", "Yesterday", "Jan 5"
 * 
 * @param dateInput - Date in various formats
 * @returns Compact relative time string (e.g., "3m ago", "2h ago", "Yesterday")
 */
export const formatVersionTimestamp = (dateInput: string | number | number[] | null | undefined): string => {
    const date = parseDate(dateInput);
    if (!date || isNaN(date.getTime())) {
        return '';
    }

    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();
    
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);

    if (isToday) {
        if (diffInMinutes < 1) {
            return 'just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        } else {
            return `${diffInHours}h ago`;
        }
    } else if (isYesterday) {
        return 'Yesterday';
    } else {
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    }
};